import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { type Dayjs } from 'dayjs';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

export interface TravelDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  minDate?: string;
  placeholder?: string;
  displaySx?: object;
  /** Show chevron inside the clickable trigger (opens calendar). */
  showChevron?: boolean;
  chevronSx?: object;
}

function formatDisplayDate(isoDate: string): string {
  return dayjs(isoDate).format('DD MMM YYYY');
}

export function TravelDatePicker({
  value,
  onChange,
  minDate,
  placeholder = 'Select date',
  displaySx,
  showChevron = false,
  chevronSx,
}: TravelDatePickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const dayjsValue = value ? dayjs(value) : null;
  const minDayjs = minDate ? dayjs(minDate) : dayjs().startOf('day');
  const display = value ? formatDisplayDate(value) : placeholder;

  const handleChange = (newValue: Dayjs | null) => {
    if (newValue?.isValid()) {
      onChange(newValue.format('YYYY-MM-DD'));
    }
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          cursor: 'pointer',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          minHeight: 22,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.9375rem',
            fontWeight: value ? 600 : 400,
            color: value ? '#0F172A' : '#94A3B8',
            lineHeight: 1.25,
            ...displaySx,
          }}
        >
          {display}
        </Typography>
        {showChevron && (
          <KeyboardArrowDownRoundedIcon
            sx={{
              fontSize: 20,
              color: '#94A3B8',
              flexShrink: 0,
              ...chevronSx,
            }}
          />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 12px 40px rgba(15, 23, 42, 0.14)',
              overflow: 'hidden',
              maxWidth: 'calc(100vw - 32px)',
            },
          },
        }}
      >
        <DateCalendar
          value={dayjsValue}
          onChange={handleChange}
          minDate={minDayjs}
          disablePast
          sx={{
            width: '100%',
            maxWidth: 320,
            '& .MuiPickersDay-root': {
              fontSize: '0.875rem',
            },
          }}
        />
      </Popover>
    </>
  );
}
